import React from "react";
import { NavLink, Outlet, useNavigate, Navigate, useLocation } from "react-router-dom";
import { LayoutDashboard, FileText, Sparkles, Calendar, Building2, Search, Target, Newspaper, LogOut, Activity, DollarSign, Heart, ScrollText, Settings, AlertTriangle, Download, Cpu, Layers, ShieldAlert, Users } from "lucide-react";
import { isAuthenticated, currentUser, logout } from "@/lib/seoApi";

const NAV = [
  { to: "/seo-studio", end: true, icon: LayoutDashboard, label: "Dashboard" },
  { to: "/seo-studio/ops", icon: Activity, label: "Operations" },
  { to: "/seo-studio/config", icon: Settings, label: "Configuration" },
  { to: "/seo-studio/ai", icon: Cpu, label: "AI Settings" },
  { to: "/seo-studio/costs", icon: DollarSign, label: "Cost Ledger" },
  { to: "/seo-studio/errors", icon: AlertTriangle, label: "Error Center" },
  { to: "/seo-studio/reports", icon: Download, label: "Reports" },
  { to: "/seo-studio/leads", icon: Users, label: "Leads" },
  { to: "/seo-studio/health", icon: Heart, label: "System Health" },
  { to: "/seo-studio/logs", icon: ScrollText, label: "Logs" },
  { to: "/seo-studio/briefs", icon: FileText, label: "Briefs" },
  { to: "/seo-studio/runs", icon: Sparkles, label: "Generation Runs" },
  { to: "/seo-studio/batches", icon: Layers, label: "Batches" },
  { to: "/seo-studio/published", icon: Newspaper, label: "Published Articles" },
  { to: "/seo-studio/review", icon: ShieldAlert, label: "Review Queue" },
  { to: "/seo-studio/plans", icon: Calendar, label: "Content Plans" },
  { to: "/seo-studio/market", icon: Building2, label: "Market Research" },
  { to: "/seo-studio/keywords", icon: Search, label: "Keywords" },
  { to: "/seo-studio/intents", icon: Target, label: "Search Intent" },
];

export default function SeoLayout() {
  const location = useLocation();
  const nav = useNavigate();
  if (!isAuthenticated()) {
    return <Navigate to="/seo-studio/login" state={{ from: location }} replace />;
  }
  const user = currentUser();
  return (
    <div className="flex min-h-screen bg-neutral-50 text-neutral-900" data-testid="seo-studio-shell">
      <aside className="w-64 bg-white border-r border-neutral-200 flex flex-col">
        <div className="px-6 py-6 border-b border-neutral-200">
          <div className="font-semibold text-lg tracking-tight">SEO Studio</div>
          <div className="text-xs text-neutral-500 mt-1">Business layer · ACF-powered</div>
        </div>
        <nav className="flex-1 py-4 space-y-1 px-3">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition ${
                  isActive
                    ? "bg-neutral-900 text-white"
                    : "text-neutral-700 hover:bg-neutral-100"
                }`
              }
            >
              <item.icon size={16} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-neutral-200 px-4 py-4 text-xs">
          <div className="truncate text-neutral-700 mb-2" title={user?.email}>
            {user?.email || "signed in"}
          </div>
          <button
            onClick={() => { logout(); nav("/seo-studio/login"); }}
            className="flex items-center gap-2 text-neutral-500 hover:text-neutral-900 transition"
            data-testid="seo-logout-btn"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-x-hidden">
        <div className="max-w-6xl px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
